import React, { Fragment, useEffect } from 'react';
import { string, arrayOf, shape, func, bool } from 'prop-types';
import PageTitle from 'components/PageTitle';
import {
  notRequiredAddressSchema as addressSchema,
  isValidUKCountryCode,
} from 'components/Address/util';
import MappedAddressRows from 'components/Address/common/MappedAddressRows';
import AddAddressButton from 'components/Address/common/AddAddressButton';
import Alert from '@johnlewispartnership/wtr-ingredients/ingredients/Alert';
import { scrollToClass } from 'components/Forms/utilities/ErrorScrolling/util';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import styles from '../ChooseAddressModal.scss';

const AddressBookView = ({
  addresses,
  hasError = false,
  onAfterSelect,
  pageTitle,
  showAddAddress,
  ukOnlyAddress = false,
}) => {
  const activeAddresses = ukOnlyAddress
    ? addresses.filter(a => isValidUKCountryCode(a.country))
    : addresses;
  const inactiveAddresses = ukOnlyAddress
    ? addresses.filter(a => !isValidUKCountryCode(a.country))
    : [];

  useEffect(() => {
    if (hasError) {
      scrollToClass('addressApiError');
    }
  }, [hasError]);

  return (
    <section className={styles.chooseAddress} data-test="choose-address-address-book">
      <div className="container-fluid">
        <PageTitle text={pageTitle} uppercase display underline id="selectAddressModalTitle" />
        <div className="row">
          <div className="col-sm-push-2of12 col-sm-8of12 col-lg-push-3of12 col-lg-6of12">
            <div className={styles.buttonRow}>
              <AddAddressButton
                onClick={showAddAddress}
                showAsLink={false}
                showAsPlaceholder={false}
              />
              {hasError && (
                <div id="addressApiError" className={styles.addressApiError}>
                  <Alert
                    type="error"
                    title="Something went wrong."
                    message="Apologies something went wrong when editing your address. Please try again or at a later time."
                    data-test="address-book-error"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {activeAddresses && activeAddresses.length > 0 && (
          <div className="row">
            <div className="col-xs-12of12">
              <MappedAddressRows
                addresses={activeAddresses}
                isEditable
                selectable
                data-test="available-addresses"
                onAfterSelect={onAfterSelect}
              />
            </div>
          </div>
        )}
        {ukOnlyAddress && inactiveAddresses.length > 0 && (
          <Fragment>
            <div className={styles.unavailableAddressesTitle}>
              <Typography element="h2" styleAs="sectionHeadingLight" textStyle="heading-xl">
                Unavailable addresses
              </Typography>
            </div>
            <div className="row">
              <div className="col-xs-12of12">
                <MappedAddressRows
                  addresses={inactiveAddresses}
                  selectable={false}
                  data-test="unavailable-addresses"
                  isEditable={false}
                  listRole="Unavailable addresses"
                />
              </div>
            </div>
          </Fragment>
        )}
      </div>
    </section>
  );
};

AddressBookView.propTypes = {
  addresses: arrayOf(shape(addressSchema)).isRequired,
  hasError: bool,
  onAfterSelect: func.isRequired,
  pageTitle: string.isRequired,
  showAddAddress: func.isRequired,
  ukOnlyAddress: bool,
};

export default AddressBookView;
